import { useAuth0 } from "@auth0/auth0-react";
import { Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PropsWithChildren } from "react";
import { Outlet } from "react-router-dom";

import {
  NEW_LAYER_BANNER_HEIGHT,
  NewLayerBanner,
  useNewLayerBannerSnooze,
} from "../../features/announcements/NewLayerBanner";
import TeamNoticesWrapper from "../../features/notices/TeamNoticesWrapper";
import { InferenceFormState } from "../inference-forms/state/InferenceFormState";
import { PrimaryNavigation } from "../nav/components/PrimaryNavigation";
// TODO: Refactor PrimaryNavigation, its structure is really not optimal
import { PrimaryNavigationContent } from "../nav/components/PrimaryNavigation/components/PrimaryNavigationContent/PrimaryNavigationContent";

export const Content = styled("main")(({ theme }) => ({
  position: "relative",
  width: "100%",
  margin: theme.spacing(2, 2, 2, 0),
  borderRadius: 5,
  border: `1px solid ${theme.palette.secondary[6]}`,
  backgroundColor: theme.palette.secondary[8],
  overflow: "auto",
  // Fill the flex row (which itself fills the viewport minus any banner above),
  // minus this element's own vertical margins (spacing(2) top + spacing(2) bottom).
  height: `calc(100% - ${theme.spacing(4)})`,
}));

/** Note:
 * InferenceFormState is expected to be in WorkspaceLayout but code crashes when doing so,
 * this is a temporary location, still better than having it in core.
 * Crashed caused by the "hacks" we have using `useLayoutEffect` to fix a race condition.
 */
export const Layout = (props: PropsWithChildren) => {
  const { children = <Outlet /> } = props;
  const { isAuthenticated } = useAuth0();
  const { isSnoozed: isBannerSnoozed } = useNewLayerBannerSnooze();
  const isBannerVisible = isAuthenticated && !isBannerSnoozed;

  // The nav Drawer is `position: fixed` and lives outside this flex flow, so it
  // can't be pushed down by the banner via layout — it takes the offset directly.
  const bannerOffset = isBannerVisible ? NEW_LAYER_BANNER_HEIGHT : 0;

  const shell = (
    <Stack direction="row" sx={{ overflow: "hidden", flex: 1, minHeight: 0 }}>
      <PrimaryNavigation topOffset={bannerOffset}>
        <PrimaryNavigationContent />
      </PrimaryNavigation>
      <Content id="page-content">{children}</Content>
    </Stack>
  );

  const viewportHeight = CSS.supports("height", "100dvh") ? "100dvh" : "100vh";

  const content = (
    <InferenceFormState>
      <Stack sx={{ height: viewportHeight, overflow: "hidden" }}>
        {isBannerVisible && <NewLayerBanner />}
        {shell}
      </Stack>
    </InferenceFormState>
  );

  return isAuthenticated ? (
    <TeamNoticesWrapper>{content}</TeamNoticesWrapper>
  ) : (
    content
  );
};
